import React, { useEffect, useState } from "react";
import { Box } from "../../../components/styled-system/Box";
import { Spinner } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { fetchSealRegistrationStatistics } from "../../../api/seal-registration-api";
import { SealRegistrationStatistics } from "../../../api/models";

const STATUS_COLORS: Record<string, string> = {
  Succeeded: "#4caf50",
  Pending: "#ff9800",
  "Failed / Expired": "#f44336",
};

const formatCurrency = (cents: number) =>
  `$${(cents / 100).toFixed(2)}`;

export const SealRegistrationsPanel: React.FC = () => {
  const [stats, setStats] = useState<SealRegistrationStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSealRegistrationStatistics()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <Spinner animation="border" role="status" />
      </Box>
    );
  }

  const statusBreakdown = stats?.statusBreakdown || [];
  const monthlyRegistrations = stats?.monthlyRegistrations || [];
  const countryBreakdown = stats?.countryBreakdown || [];
  const latestRegistrations = stats?.latestRegistrations || [];

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "32px" }}>
        <SummaryCard label="Total Registrations" value={stats?.totalRegistrations ?? 0} />
        <SummaryCard label="Successful" value={stats?.succeededCount ?? 0} color="#4caf50" />
        <SummaryCard label="Pending" value={stats?.pendingCount ?? 0} color="#ff9800" />
        <SummaryCard label="Failed / Expired" value={stats?.failedCount ?? 0} color="#f44336" />
        <SummaryCard label="Total Revenue" value={formatCurrency(stats?.totalRevenueCents ?? 0)} color="#184472" />
        <SummaryCard label="Total Donations" value={formatCurrency(stats?.totalDonationsCents ?? 0)} color="#184472" />
      </div>

      {/* Charts row */}
      <div style={{ display: "flex", gap: "32px", flexWrap: "wrap", marginBottom: "32px" }}>
        {/* Status pie chart */}
        <div style={{ flex: "1", minWidth: "300px" }}>
          <h3 className="title-small">Registration Status</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={statusBreakdown}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                startAngle={90}
                endAngle={-270}
                label={({ name, value }: any) => `${name}: ${value}`}
              >
                {statusBreakdown.map((entry) => (
                  <Cell
                    key={entry.name}
                    fill={STATUS_COLORS[entry.name] || "#616161"}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly bar chart */}
        {monthlyRegistrations.length > 0 && (
          <div style={{ flex: "1.5", minWidth: "400px" }}>
            <h3 className="title-small">Successful Registrations by Month</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyRegistrations}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#184472" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Country bar chart */}
      {countryBreakdown.length > 0 && (
        <div style={{ marginBottom: "32px" }}>
          <h3 className="title-small">Registrations by Country</h3>
          <ResponsiveContainer width="100%" height={Math.max(200, countryBreakdown.length * 35)}>
            <BarChart data={countryBreakdown} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="country" fontSize={13} width={110} />
              <Tooltip />
              <Bar dataKey="count" fill="#184472" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Latest 10 table */}
      <h3 className="title-small">Last 10 Submissions</h3>
      <Table hover size="sm" className="vineyard-table input-text u-mt-large">
        <thead>
          <tr>
            <th>Date</th>
            <th>Email</th>
            <th>Status</th>
            <th>Legal Organization</th>
            <th>Name Registered</th>
            <th>Country</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {latestRegistrations.map((row) => (
            <tr key={row.id}>
              <td>{new Date(row.createdDate).toLocaleDateString()}</td>
              <td>{row.email}</td>
              <td>
                <span style={{
                  color: row.paymentStatus === "succeeded" ? "#4caf50"
                    : row.paymentStatus === "pending" ? "#ff9800"
                    : "#f44336",
                  fontWeight: 600,
                }}>
                  {row.paymentStatus}
                </span>
              </td>
              <td>{row.legalOrganizationName}</td>
              <td>{row.registeredName}</td>
              <td>{row.organizationCountry}</td>
              <td>{formatCurrency(row.totalAmountCents || 0)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const SummaryCard = ({ label, value, color }: { label: string; value: string | number; color?: string }) => (
  <div
    style={{
      border: "1px solid #ddd",
      borderRadius: "6px",
      padding: "16px 20px",
      minWidth: "160px",
      flex: "1",
    }}
  >
    <div style={{ fontFamily: "'Alegreya Sans', sans-serif", fontSize: "13px", color: "#616161", marginBottom: "4px" }}>
      {label}
    </div>
    <div style={{ fontFamily: "'Alegreya Sans', sans-serif", fontSize: "28px", fontWeight: 700, color: color || "#212121" }}>
      {value}
    </div>
  </div>
);
